import * as storage from './storage.js';

export const CALCULATOR_VALUES_STORAGE_KEY = 'calculatorValues';

type StoredCalculatorValues = Record<string, Record<string, unknown>>;

export interface CalculatorValueInputSpec {
  key: string;
  type?: {
    kind: 'number' | 'angle';
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
  };
}

export function getStoredCalculatorValues(
  calculatorId: string,
  defaults: Record<string, number>,
  specs: readonly CalculatorValueInputSpec[] = [],
): Record<string, number> {
  const stored = storage.get<StoredCalculatorValues>(CALCULATOR_VALUES_STORAGE_KEY);
  const values = stored?.[calculatorId];
  if (!values || typeof values !== 'object') return { ...defaults };

  const next = { ...defaults };
  const specByKey = new Map(specs.map((spec) => [spec.key, spec]));
  for (const key of Object.keys(defaults)) {
    const value = values[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      next[key] = normalizeCalculatorInputValue(value, specByKey.get(key));
    }
  }
  return next;
}

export function normalizeCalculatorInputValue(
  value: number,
  spec?: CalculatorValueInputSpec,
): number {
  if (!Number.isFinite(value)) return value;

  let next = value;
  if (isIntegerCountSpec(spec)) next = Math.round(next);

  const min = spec?.type?.min;
  const max = spec?.type?.max;
  if (typeof min === 'number') next = Math.max(min, next);
  if (typeof max === 'number') next = Math.min(max, next);
  return next;
}

export function setStoredCalculatorValues(
  calculatorId: string,
  values: Record<string, number>,
): void {
  const stored = storage.get<StoredCalculatorValues>(CALCULATOR_VALUES_STORAGE_KEY) ?? {};
  storage.set(CALCULATOR_VALUES_STORAGE_KEY, {
    ...stored,
    [calculatorId]: pickFiniteNumbers(values),
  });
}

export function clearStoredCalculatorValues(calculatorId: string): void {
  const stored = storage.get<StoredCalculatorValues>(CALCULATOR_VALUES_STORAGE_KEY);
  if (!stored?.[calculatorId]) return;
  const next = { ...stored };
  delete next[calculatorId];
  storage.set(CALCULATOR_VALUES_STORAGE_KEY, next);
}

function pickFiniteNumbers(values: Record<string, number>): Record<string, number> {
  const next: Record<string, number> = {};
  for (const [key, value] of Object.entries(values)) {
    if (Number.isFinite(value)) next[key] = value;
  }
  return next;
}

function isIntegerCountSpec(spec?: CalculatorValueInputSpec): boolean {
  return spec?.type?.kind === 'number' && spec.type.unit === '' && spec.type.step === 1;
}
